<template>
  <div>
    <Loading v-if="isLoading" type="3"></Loading>
    <div v-else>
      <h4 class="mb-1" id="title">{{ "adminDashboard.clients" | i18n }}</h4>
      <p class="d-flex justify-content-center m-0">
        <b-button
          v-b-toggle="'client-list'"
          id="btn-collapse"
          class="bg-transparent border-0 shadow-none"
          @click="listIsClicked()"
        >
          <h3 class="m-0 my-1 text-primary text-center">
            {{ "adminDashboard.clients" | i18n }}
            <i
              class="icon text-primary"
              :class="isClicked ? 'caret down' : 'caret right'"
            ></i>
          </h3>
        </b-button>
      </p>
      <b-collapse :visible="visible" id="client-list">
        <InputText
          identifier="searchCriteria"
          :label="'adminDashboard.search' | i18n"
          icon="search"
          @onInput="setSearchCriteria"
        />
        <b-row class="mt-4 mb-3" no-gutters="">
          <div
            class="font-weight-bold mr-5 pointer disable-select"
            @click="toggleFilter"
          >
            {{ "tableKeys.nom" | i18n }}

            <i
              class="caret icon pointer"
              :class="
                filters.length > 0
                  ? filters[0].order == 'asc'
                    ? 'down'
                    : 'up'
                  : ''
              "
            ></i>
          </div>
        </b-row>
        <div class="scrollable-section mb-3">
          <div
            v-for="(client, index) in computedFilteredClients"
            :key="index"
            @click="emitOnClientClick(client)"
            class="client-row pointer py-2 py-lg-1"
          >
            <b-link :to="'clients/' + client.id" class="client-link">
              <b-row no-gutters="" align-h="between">
                <b-col cols="9">
                  <div class="hover text-truncate text-dark">
                    {{ client.nom }}
                  </div>
                </b-col>
                <b-col cols="3" class="d-flex justify-content-end">
                  <i class="address book icon"></i>
                </b-col>
              </b-row>
            </b-link>
          </div>
        </div>
        <GenericBtnAdd @click="addClient" />
        <FormClientSection
          id="FormClientSection"
          :isShown="show"
          v-on:canceled="close"
          @successed-request="close"
          title="Ajouter un client"
          :action="this.$api.addClient"
        />
      </b-collapse>
    </div>
  </div>
</template>

<script>
import { InputText, GenericBtnAdd } from "@/components";
import FormClientSection from "../forms/FormClientSection.vue";
import Loading from "../../components/others/Loading.vue";

/**
 * @description Permet d'avoir la liste des clients pour le tableau de bord admin de Radar
 * @author Samuel Dubé
 * @author Samuel Colassin
 * @author Kevin Dubé
 * @author Francine Jin
 * @vue-data {Boolean} show - Définit si le modal est affiché ou non
 * @vue-data {Array} clients - La liste des clients de Radar
 * @vue-data {Array} filters - Les filtres (tri) à appliquer à la liste de clients
 * @vue-data {Object} sortsFunction - Les fonctions de tri disponibles
 * @vue-data {String} searchCriteria - Le texte entré dans la barre de rercherche par l'utilisateur
 * @vue-data {Boolean} isLoading - Si la page est en train de charger
 * @vue-data {Boolean} isClicked - Si le bouton est cliqué
 * @vue-data {Boolean} visible - Si la liste des clients est visible
 * @vue-computed {Object} computedFilteredClients - Permet de rechercher et d'appliquer le filtre voulu
 * @vue-event {Object} onClientClick - Est émis lorsqu'un élément de la liste est cliqué
 */
export default {
  components: {
    InputText,
    FormClientSection,
    GenericBtnAdd,
    Loading
  },
  name: "ListClientsSection",
  data() {
    return {
      show: false,
      clients: [],
      filters: [{ key: "ville", order: "asc" }],
      sortsFunction: {
        asc: (a, b) => a.nom.localeCompare(b.nom),
        desc: (a, b) => b.nom.localeCompare(a.nom)
      },
      searchCriteria: "",
      isLoading: true,
      isClicked: false,
      visible: true
    };
  },
  computed: {
    computedFilteredClients() {
      let filteredClients = this.clients.filter(c =>
        c.nom.toLowerCase().includes(this.searchCriteria.toLowerCase())
      );

      const order = this.filters[0].order;
      filteredClients.sort(this.sortsFunction[order]);

      return filteredClients;
    }
  },
  /**
   * @description Obtient la liste de clients et applique la mise en page selon la taille de la fenêtre
   */
  mounted() {
    this.getClients();
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  },
  /**
   * @description Détruit l'événement
   */
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    /**
     * @description Permet de fermer le modal
     */
    close() {
      this.show = false;
    },
    /**
     * @description Permet d'ouvrir le modal
     */
    addClient() {
      this.show = true;
    },
    /**
     * @description Selon la taille de la fenêtre, la liste des clients s'affichera différemment
     */
    handleResize() {
      if (window.innerWidth > 991) {
        this.isClicked = false;
        this.visible = true;
      } else {
        this.isClicked = false;
        this.visible = false;
      }
    },
    /**
     * @description Émet un évenement lorsqu'un élément de la liste est cliqué
     * @param {Object} client Le client sur lequel l'utilisateur a cliqué
     */
    emitOnClientClick(client) {
      this.$emit("onClientClick", client);
    },
    /**
     * @description Effectue une requête pour obtenir la liste des clients
     */
    getClients() {
      this.$api
        .getClientNotPaginated()
        .then(res => {
          if (res.data && res.data.data) {
            this.clients = res.data.data;
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isLoading = false;
        });
    },
    /**
     * @description Applique un filtre ascendant ou descendant à la liste de client
     */
    toggleFilter() {
      this.filters.find(f => f.order === "asc") != undefined
        ? (this.filters = [{ key: "ville", order: "desc" }])
        : (this.filters = [{ key: "ville", order: "asc" }]);
    },
    /**
     * @description Applique la recherche courante de l'utilisateur
     * @param {String} eventData Le texte entré dans la barre de recherche par l'utilisateur
     */
    setSearchCriteria(eventData) {
      this.searchCriteria = eventData.value;
    },
    /**
     * @description Change la valeur de isClicked
     */
    listIsClicked() {
      this.isClicked = !this.isClicked;
    }
  }
};
</script>
<style lang="scss" scoped>
#btn-collapse {
  display: none;
}

@media screen and (max-width: 1025px) {
  .scrollable-section {
    max-height: 16.5rem !important;
  }
}
@media screen and (max-width: 991px) {
  #btn-collapse {
    display: inline;
  }
  #title {
    display: none;
  }
}
@media screen and (max-width: 414px) {
  #btn-collapse {
    display: inline;
  }
  #title {
    display: none;
  }
}

.client-row:hover {
  .hover {
    font-weight: 500;
  }
}

.scrollable-section {
  max-height: 33.5rem;
  overflow: scroll;
}

.client-link {
  text-decoration: none !important;
}

.client-row {
  font-size: 1.1em;
}

.scrollable-section::-webkit-scrollbar {
  display: none;
}
</style>
